import { useMemo, useState } from 'react'
import { AgentAPI } from '@/api/agent'
import { useUI } from '@/store/ui'
import { Markdown } from '@/lib/markdownView'
import { HelpHint } from '@/components/HelpHint'
import {
  egoKindGloss,
  parseTalaReply,
  toSnakeWord,
  type ParsedTalaReply,
  type TalaSuggestion,
} from '@/lib/talaRefine'

// "✦ Ask Tala to refine" — the ONE refine affordance, shared by the domain builder rail,
// first-run onboarding, and the inspect panel's edit path. Tala's prose renders through the
// same safe <Markdown> as mission artifacts (never raw text), and the reply's machine block
// (see lib/talaRefine.ts) becomes apply chips: per-chip "+ add" plus "Add all". A reply with
// no machine block degrades to rendered prose — advisory, exactly as before.

/** No graph citations in refine advice — [[names]] degrade to plain text. */
const NO_CITES = new Map<string, never>()
const noPick = () => {}

const KIND_MARK: Record<TalaSuggestion['kind'], string> = {
  entity: 'entity',
  edge: 'edge',
  ego: 'ego',
}

export interface TalaRefineProps {
  /** Which refine surface this is — the backend keys the machine-block instruction off it. */
  surface: 'domains' | 'onboarding'
  domainName: string
  entityTypes: string[]
  relationTypes: string[]
  /** Normalized ego relation names already on the draft/domain. */
  egoNames: string[]
  /**
   * Merge suggestions into the draft (or PATCH them into the live domain). Receives one
   * suggestion for a chip click, several for "Add all". May be async — chips stay disabled
   * while it runs; a throw surfaces as the component's error line.
   */
  onApply: (suggestions: TalaSuggestion[]) => void | Promise<void>
  buttonClassName?: string
  buttonStyle?: React.CSSProperties
  /** Container class for Tala's rendered prose (defaults to a quiet inline style). */
  replyClassName?: string
}

export function TalaRefine({
  surface,
  domainName,
  entityTypes,
  relationTypes,
  egoNames,
  onApply,
  buttonClassName = 'dw-action-btn',
  buttonStyle,
  replyClassName,
}: TalaRefineProps) {
  const { diwaUrl } = useUI()
  const agent = useMemo(() => new AgentAPI(diwaUrl), [diwaUrl])
  const [busy, setBusy] = useState(false)
  const [applying, setApplying] = useState(false)
  const [reply, setReply] = useState<ParsedTalaReply | null>(null)
  const [error, setError] = useState<string | null>(null)

  const have = useMemo(() => {
    return {
      entity: new Set(entityTypes.map(toSnakeWord)),
      edge: new Set(relationTypes.map(toSnakeWord)),
      ego: new Set(egoNames.map(toSnakeWord)),
    }
  }, [entityTypes, relationTypes, egoNames])

  const applied = (s: TalaSuggestion) => have[s.kind].has(s.name)
  const pending = reply?.suggestions.filter((s) => !applied(s)) ?? []

  async function ask() {
    if (busy) return
    setBusy(true)
    setError(null)
    setReply(null)
    try {
      const opening =
        surface === 'onboarding'
          ? `I'm just getting started in Diwa — a relationship-intelligence graph — and shaping my first domain`
          : `I'm shaping a domain of my world in Diwa — a relationship-intelligence graph —`
      const prompt =
        `${opening} called "${domainName.trim() || 'my domain'}". So far it has ` +
        `entity types [${entityTypes.join(', ') || 'none'}], ` +
        `edge types [${relationTypes.join(', ') || 'none'}], and ` +
        `Me→entity ego relations [${egoNames.join(', ') || 'none'}]. ` +
        `Suggest a few more entity types, edge types, and ego relations I might be missing — ` +
        `briefly say why, then list the concrete additions.` +
        (surface === 'onboarding' ? ' Be terse and encouraging.' : ' Be terse.')
      const r = await agent.ask(prompt, { surface })
      setReply(parseTalaReply(r.answer))
    } catch (e) {
      const status = (e as { status?: number }).status
      setError(
        status === 503
          ? 'Tala needs a model key — set one in Settings for live suggestions.'
          : status === 502
            ? 'Tala is unreachable right now — the curated suggestions still stand.'
            : "Couldn't reach Tala — try again.",
      )
    } finally {
      setBusy(false)
    }
  }

  async function apply(suggestions: TalaSuggestion[]) {
    if (applying || suggestions.length === 0) return
    setApplying(true)
    setError(null)
    try {
      await onApply(suggestions)
    } catch {
      setError("Couldn't apply that suggestion — nothing changed. Try again.")
    } finally {
      setApplying(false)
    }
  }

  return (
    <>
      <button type="button" className={buttonClassName} style={buttonStyle} disabled={busy} onClick={ask}>
        {busy ? 'Asking Tala…' : '✦ Ask Tala to refine'}
      </button>

      {reply && reply.prose && (
        <div
          className={replyClassName}
          style={
            replyClassName
              ? undefined
              : {
                  marginTop: 6,
                  fontSize: 'calc(11px * var(--ae-text-scale, 1))',
                  lineHeight: 1.5,
                  color: 'var(--ae-color-text-secondary)',
                }
          }
        >
          <Markdown text={reply.prose} byName={NO_CITES} onPick={noPick} />
        </div>
      )}

      {reply && reply.suggestions.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div className="dw-chip-row">
            {reply.suggestions.map((s) => {
              const done = applied(s)
              const chip = (
                <button
                  key={`${s.kind}:${s.name}`}
                  type="button"
                  className="dw-chip"
                  disabled={done || applying}
                  title={
                    done
                      ? `${s.name} — already added`
                      : s.why
                        ? `${s.why} — click to add`
                        : `add ${s.name}`
                  }
                  style={{ cursor: done ? 'default' : 'pointer', opacity: done ? 0.4 : 1 }}
                  onClick={() => void apply([s])}
                >
                  <span aria-hidden="true" style={{ opacity: 0.6, marginRight: 4 }}>
                    {s.kind === 'ego' ? `ego·${s.egoKind}` : KIND_MARK[s.kind]}
                  </span>
                  {s.name}
                  {!done && <span aria-hidden="true" style={{ marginLeft: 4, opacity: 0.6 }}>+</span>}
                </button>
              )
              // ego chips carry the event/state gloss — the kind is a real concept, not jargon
              return s.kind === 'ego' ? (
                <HelpHint key={`${s.kind}:${s.name}`} gloss={egoKindGloss(s.egoKind)}>
                  {chip}
                </HelpHint>
              ) : (
                chip
              )
            })}
          </div>
          {pending.length > 1 && (
            <button
              type="button"
              className="dw-inline-link"
              style={{ marginTop: 6 }}
              disabled={applying}
              onClick={() => void apply(pending)}
            >
              + Add all {pending.length}
            </button>
          )}
        </div>
      )}

      {error && (
        <div className="dw-inbox-detail" style={{ marginTop: 6, fontSize: 'calc(11px * var(--ae-text-scale, 1))' }}>
          {error}
        </div>
      )}
    </>
  )
}
